import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Wallet, TrendingUp, GraduationCap, Heart, Star, DollarSign, ShieldCheck, LucideIcon } from "lucide-react";
import { useCms } from "@/hooks/useCms";

const ICON_MAP: Record<string, LucideIcon> = {
  DollarSign, GraduationCap, TrendingUp, Heart, Star, Wallet, ShieldCheck,
};

const DEFAULT_FEATURES = [
  { icon: "DollarSign", title: "Talent Development Sponsorship", desc: "We connect promising athletes with sponsors willing to invest in their development — covering training, travel, and accommodation costs." },
  { icon: "GraduationCap", title: "Sports Scholarships", desc: "Partnerships with institutions across Europe and North America offer fully and partially funded scholarship pathways for qualifying athletes." },
  { icon: "TrendingUp", title: "Career Investment Initiatives", desc: "Structured financial support programmes helping athletes pursue professional careers without the burden of self-funding." },
  { icon: "Heart", title: "Welfare & Emergency Support", desc: "A dedicated welfare fund for athletes facing unexpected hardship — because no career should stall due to personal crisis." },
  { icon: "Star", title: "Investor Matchmaking", desc: "We connect athletes with sports investors, angel funders, and development funds looking to back rising talent." },
  { icon: "Wallet", title: "Financial Literacy", desc: "Workshops and advisory sessions helping athletes understand contracts, manage earnings, and plan for life after sport." },
];

const DEFAULT_BULLETS = [
  "Comprehensive sponsorship and investment matching programmes",
  "Scholarship opportunities with partner academies and universities",
  "Zero upfront fee model for eligible development candidates",
  "Professional financial education and contract guidance",
  "Dedicated athlete welfare fund for emergencies and critical needs",
];

export default function CareerInvestment() {
  const { getMeta, getText } = useCms();
  const meta = getMeta<{
    heading?: string;
    highlight?: string;
    subtext?: string;
    heroImage?: string;
    overviewHeading?: string;
    overviewText?: string;
    overviewImage?: string;
    bullets?: string[];
    features?: { icon: string; title: string; desc: string }[];
  }>("service_investment", {});

  const heading = meta.heading ?? "CAREER";
  const highlight = meta.highlight ?? "INVESTMENT";
  const subtext = meta.subtext ?? getText("service_investment", "Removing financial barriers so talented athletes can pursue their dreams — backed by real investment, sponsorship, and scholarship opportunities.");
  const heroImage = meta.heroImage || "https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=1600&auto=format&fit=crop";
  const overviewHeading = meta.overviewHeading ?? "TALENT SHOULD NEVER GO TO WASTE";
  const overviewText = meta.overviewText ?? "One of the biggest barriers facing talented athletes in Africa and beyond is financial. Training costs, travel expenses, visa fees, and living costs in a new country can make a professional career feel out of reach — even for the most gifted players.\n\nEliada Sports Agency's Career Investment programme directly addresses this through sponsorship matching, scholarship facilitation, investor connections, and a dedicated welfare fund. We believe every talented athlete deserves an equal opportunity to succeed.";
  const overviewImage = meta.overviewImage || "https://images.unsplash.com/photo-1522778526097-ce0a22cdbec6?w=900&auto=format&fit=crop";
  const bullets = (Array.isArray(meta.bullets) && meta.bullets.length > 0) ? meta.bullets : DEFAULT_BULLETS;
  const features = (Array.isArray(meta.features) && meta.features.length > 0) ? meta.features : DEFAULT_FEATURES;

  return (
    <>
      <SeoHead
        title={`${heading} ${highlight}`}
        description={subtext}
        path="/services/career-investment"
      />
      <PageTransition className="pt-24 pb-0">
        {/* Hero */}
        <div className="relative min-h-[55vh] flex items-center overflow-hidden">
          <img src={heroImage} alt={`${heading} ${highlight}`} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/40 bg-primary/10 text-primary mb-6">
              <Wallet size={14} />
              <span className="text-xs font-display tracking-widest uppercase font-semibold">Our Services</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-6 max-w-2xl leading-tight">
              {heading} <span className="text-primary">{highlight}</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-xl mb-8 leading-relaxed">
              {subtext}
            </p>
            <Link href="/join" onClick={() => {}}>
              <Button size="lg" className="gap-2">Apply for Support <ArrowRight size={18} /></Button>
            </Link>
          </div>
        </div>

        {/* Overview */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                  {overviewHeading}
                </h2>
                <div className="space-y-4 mb-8">
                  {overviewText.split("\n\n").map((para, i) => (
                    <p key={i} className="text-gray-600 text-lg leading-relaxed">
                      {para}
                    </p>
                  ))}
                </div>
                <ul className="space-y-3">
                  {bullets.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-700">
                      <CheckCircle className="text-primary shrink-0 w-5 h-5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <img src={overviewImage} alt="Career Investment" className="rounded-2xl shadow-2xl w-full object-cover" />
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-24 bg-secondary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
                WHAT WE <span className="text-primary">OFFER</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Comprehensive financial and developmental support for promising talent.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => {
                const IconComponent = ICON_MAP[f.icon] || Wallet;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-background p-8 rounded-2xl border border-white/5 hover:border-primary/40 transition-colors"
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5">
                      <IconComponent className="text-primary w-6 h-6" />
                    </div>
                    <h3 className="font-display font-bold text-white text-lg mb-3">{f.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">DON'T LET FINANCES STOP YOUR DREAM</h2>
            <p className="text-xl text-gray-800 mb-10 font-medium">Apply today to be considered for career investment, sponsorship, and scholarship opportunities.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link href="/join" onClick={() => {}}><Button size="lg" className="bg-gray-900 text-white hover:bg-black">Apply for Investment</Button></Link>
              <Link href="/contact" onClick={() => {}}><Button size="lg" variant="outline" className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white bg-transparent">Contact Our Team</Button></Link>
            </div>
          </div>
        </section>
      </PageTransition>
    </>
  );
}
